import { createAsyncThunk } from "@reduxjs/toolkit";
import { message } from 'antd';
import graphQLClient from "../graphql-client";
import { RootState } from "../redux/store";
import { getApiErrorMessage } from "../utils/apiError";
import {
  getProductSelectionClientId,
  readStoredProductSelection,
  StoredProductSelection,
  writeStoredProductSelection,
} from "../utils/productSelectionStorage";
import { 
  GET_PRODUCTS, 
  GET_TYPE_PRODUCTS, 
  GET_PRODUCTS_BY_TYPE, 
  GET_PRODUCTS_BY_GENDER,
  CURRENT,
  GET_USER_SHIPPING,
  ESTIMATE_ZIP_TAX,
  GET_MARKETING_SUBSCRIBERS,
  GET_LIKED_PRODUCT_BYID,
  GET_PRODUCT_BYID,
  GET_CART_ITEMS_BY_USER,
  GET_COMMENTS_BY_PRODUCT,
  CHECK_EXISTING_COMMENT,
  GET_IMG_BY_MATERIAL_FOR_PRODUCT,
  GET_COUNTRIES,
  GET_PROVINCES_BY_COUNTRY_NAME,
  GET_CART_ITEMS_COUNT,
  GET_LIKED_PRODUCT_COUNT,
  GET_IMAGES_BY_MATERIAL_FOR_HOME_PAGE,
  GET_PRODUCT_DETAILS,
  GET_SEARCHED_PRODUCT,
  GET_PAYD_ORDERS,
  GET_ADMIN_PAYD_ORDERS,
  GET_PRICE_IMAGE_FOR_PAYD_ORDER,
  CREATE_SHIPPING_ADDRESS,
  LOGIN,
  REGISTER,
  LIKED_PRODUCT,
  REMOVE_LIKE_FROM_PRODUCT,
  LOGOUT,
  CREATE_CART_ITEM,
  DELETE_CART_ITEM,
  UPDATE_CART_ITEM_QUANTITY,
  CREATE_PAYD_ORDER,
  DELETE_ALL_CART_ITEM,
  CREATE_PAYMENT_INTENT,
  CREATE_PAYPAL_ORDER,
  CAPTURE_PAYPAL_ORDER,
  FINALIZE_PAYMENT_INTENT_ORDER,
  UPDATE_CART_ITEM,
  CREATE_COMMENT,
  CREATE_COMMENT_ITEM,
  CREATE_COMMENT_IMAGE,
  RECORD_PRODUCT_VIEW,
  RESET_PASSWORD_EMAIL,
  RESET_PASSWORD_BY_TOKEN,
  UPDATE_PRODUCT_SIZE,
  UPDATE_PRODUCT_MATERIAL,
  SAVE_PRODUCT_SELECTION,
  UPDATE_CART_ITEM_SIZE,
  UPDATE_CART_ITEM_MATERIAL, 
  UPDATE_SHIP_ADRESS, 
  UPDATE_SHIP_ADRESS_CHECK_BOX, 
  DELETE_SHIPPING_ADDRESS,
  UPDATE_EMAIL_PREFERENCE,
  UPDATE_USER_AVATAR,
  SUBSCRIBE_MARKETING_EMAIL,
  GET_MATERIAL_ICONS_BY_PRODUCT_IDS,
  GET_MATERIAL_END_IMG_FOR_CART_ITEM,
  GET_CART_ITEM_SELECTES_MAT_SIZE,
  GET_PRODUCT_SELECTION,
  GET_PRODUCT_SELECTIONS,
  GET_ADMIN_PRODUCTS,
  GET_ADMIN_PRODUCT_MATERIAL_DETAILS,
  GET_ADMIN_PRODUCT_VARIANT_INVENTORIES,
  GET_ADMIN_USERS,
  GET_PRODUCT_TYPES,
  GET_SIZES,
  GET_MATERIAL_ICONS,
  CREATE_PRODUCT,
  CREATE_PRODUCT_VARIANT_INVENTORY,
  CREATE_PRODUCT_IMAGE,
  CREATE_PRODUCT_ANIMATION,
  DELETE_PRODUCT_IMAGE,
  UPDATE_MATERIAL_PRICE_STOCK,
  UPDATE_PRODUCT_VARIANT_INVENTORY_STOCK,
  UPDATE_PRODUCT_VARIANT_INVENTORY_AVAILABILITY,
  UPDATE_MATERIAL_PRICE_PRICE,
  UPDATE_MATERIAL_PRICE_AVAILABILITY,
  UPDATE_MATERIAL_PRICE_SIZES,
  UPDATE_USER_ADMIN_STATUS,
  DELETE_PRODUCT_CASCADE,
} from '../graphql';

import {
  IProductId, 
  IProductRes, 
  IType, 
  User, 
  LoginRes, 
  LoginReq, 
  RegRes, 
  IshipAddres, 
  ZipTaxAddressInput,
  ShipReq,
  CurUserPayload,
  IVariables,
  UpdateCartItemsRes,
  ThunkApiConfig,
  DeleteCartItemRes,
  DelCartItemRes,
  DelCartItemArgs,
  FetchCommentsForProdRes,
  Comment,
  CommentItem,
  FetchMaterialPriceArgs,
  FetchUserError,
  ICartItem,
  State,
  CountriesRes,
  Country,
  StatesRes,
  StateArgs,
  ResetPasswordRes,
  ProductDetailsRes,
  FetchProductDetailsArgs,
  UpdateProductRes,
  UpdateProductArgs,
  UpProductMaterialRes,
  UpdateProductMatArgs,
  UpdateCartItem,
  UpdateCartItemSizeParams,
  UpdateCartItemSizeResponse,
  UpdateCartItemMaterialResponse,
  UpdateCartItemMaterialParams,
  ShipAddressRes,
  ShipAddressWhereUniqueInput,
  ShipAddressUpdateInput,
  ShipAddressCreateInput,
  ImageWithPublicUrl,
  IProductsState,
  paydOrder,
  MaterialPrice,
  GetMaterialPriceAndImagesParams, 
  GetMaterialPriceAndImagesRes, 
  IProducts,
  DeleteCartItem,
  IProductMaterialIcons,
  MaterialImagesState,
  MaterialPriceAndImages,
  GetMaterialImageRes,
  FetchMaterialIconsArgs,
  GetCartItemsByIdsRes,
  CartItemInfo,
  IProductTypes,
  AdminProduct,
  AdminProductFormValues,
  AdminProductOptionsResponse,
  AdminProductsQueryResponse,
  AdminProductsQueryVariables,
  CreateAdminProductResponse,
  MaterialFormItem,
 SizeOption,
 } from '../components/interfaces';
import { getProductSizeValues } from '../utils/sizeOptions';

const withRequestTimeout = async <T>(
  request: Promise<T>,
  timeoutMessage = 'Request timed out',
  timeoutMs = 12000
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
  });

  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const mapRegisterError = (error: unknown): string => {
  const fallback = 'Registration failed';
  const maybeError = error as any;
  const gqlMessage =
    maybeError?.response?.errors?.[0]?.message ||
    maybeError?.message ||
    '';
  const normalized = String(gqlMessage).toLowerCase();

  if (normalized.includes('user with this email already exists')) {
    return 'This email is already registered';
  }

  if (normalized.includes('invalid email address')) {
    return 'Please enter a valid email address';
  }

  if (normalized.includes('already exists')) {
    return 'This email is already registered';
  }

  if (normalized.includes('failed to fetch') || normalized.includes('network')) {
    return 'Network error. Please try again';
  }

  return gqlMessage || fallback;
};

const normalizeName = (value: string) => value.trim().toLowerCase();

const uniqueByName = <T extends { name: string }>(items: T[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const normalized = normalizeName(item.name || '');
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

const requestStatesByCountryName = async (countryName: string): Promise<State[]> => {
  const normalized = String(countryName || '').trim().toLowerCase();
  const countryAliases =
    normalized === 'united states' ||
    normalized === 'united states of america' ||
    normalized === 'usa' ||
    normalized === 'us'
      ? ['USA', 'United States', 'United States of America', 'US']
      : [countryName];

  const statesByName = await Promise.all(
    countryAliases.map(async (alias) => {
      const variables = {
        where: {
          country: {
            name: {
              equals: alias,
            },
          },
        },
      };

      const response = await graphQLClient.request<StatesRes>(
        GET_PROVINCES_BY_COUNTRY_NAME,
        variables
      );

      return response.states || [];
    })
  );

  return uniqueByName(
    statesByName.flat().filter(state => Boolean(state?.name?.trim()))
  ).sort((a, b) => a.name.localeCompare(b.name));
};

 export const fetchProducts = createAsyncThunk<
 { products: IProducts[]; take: number; skip: number; filterKey: string },
 { skip: number; take: number; gender?: string; type?: string; filterKey?: string }
>(
 'Products/fetchProducts',
 async ({ skip, take, gender, type, filterKey = 'all' }, { rejectWithValue }) => {
   try {
     const filters = [
       gender ? { gender: { equals: gender } } : null,
       type ? { productType: { name: { equals: type } } } : null,
     ].filter(Boolean);
     const where = filters.length ? { AND: filters } : {};
     const response = await graphQLClient.request<{
       products: IProducts[];
     }>(GET_PRODUCTS, { skip, take, where });

     if (!response?.products || !Array.isArray(response.products)) {
       return rejectWithValue('Response data is missing products');
     }

     const products = response.products.map((product) => ({
         id: product.id,
         title: product.title,
         selectedMaterial: product.selectedMaterial,
         gender: product.gender,
         productTypeName: product.productType?.name ?? product.productTypeName,
       }));

     return {
       products: await mergeProductsWithPersistedSelections(products),
       take,
       skip,
       filterKey,
     };
   } catch (error) {
     console.error('Error fetching products:', error);
     return rejectWithValue(
       error instanceof Error ? error.message : 'Unknown error'
     );
   }
 }
);

export const fetchTypeProducts = createAsyncThunk<
  IProductTypes[],
  void,
  { rejectValue: string }
>(
  'Products/GetTypeProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response: IType = await graphQLClient.request(GET_TYPE_PRODUCTS);

      const map = new Map<string, IProductTypes>();

      response.products.forEach((product) => {
        const type = product.productType;

        if (!map.has(type.name)) {
          map.set(type.name, {
            name: type.name,
            image: type.image
          });
        }
      });

      return Array.from(map.values());

    } catch (error) {
      console.error('Error fetching type products:', error);
      return rejectWithValue('Error fetching product types');
    }
  }
);

export const fetchMaterialIconsByProductIds = createAsyncThunk<
  MaterialImagesState,
  FetchMaterialIconsArgs,
  { rejectValue: string }
>(
  'products/fetchMaterialIconsByProductIds',
  async ({ ids, source }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        products: IProductMaterialIcons[];
      }>(GET_MATERIAL_ICONS_BY_PRODUCT_IDS, { ids });

      if (!response.products) {
        return rejectWithValue('No products found for given IDs');
      }

      const materialIconsByProductId: MaterialImagesState = {};
      response.products.forEach((product) => {
        materialIconsByProductId[product.id] = product.materialPrices
          .filter((mp) => mp?.materialIcon?.icon)
          .map((mp) => ({
            name: mp.materialIcon.materialName,
            image: {
              id: mp.materialIcon.icon.id,
              publicUrl: mp.materialIcon.icon.publicUrl,
            },
          }));
      });

      return materialIconsByProductId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchFilteredProducts = createAsyncThunk<
  { products: IProducts[]; type: string; take: number; skip: number },
  { type: string; skip: number; take: number }
>(
  'Products/fetchFilteredProducts',
  async ({ type, skip, take }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<
        { products: IProducts[] }
      >(GET_PRODUCTS_BY_TYPE, { type, skip, take });

      if (!response?.products || !Array.isArray(response.products)) {
        return rejectWithValue(
          'Response data is missing products'
        );
      }

      const products = response.products.map((product) => ({
          id: product.id,
          title: product.title,
          selectedMaterial: product.selectedMaterial,
        }));

      return {
        products: await mergeProductsWithPersistedSelections(products),
        type,
        take,
        skip,
      };
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Error fetching filtered products'
      );
    }
  }
);

export const fetchGenderProducts = createAsyncThunk<
  { products: IProducts[]; gender: string; take: number; skip: number },
  { gender: string; skip: number; take: number }
>(
  'Products/fetchGenderProducts',
  async ({ gender, skip, take }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<
        { products: IProducts[] }
      >(GET_PRODUCTS_BY_GENDER, { gender, skip, take });

      if (!response?.products || !Array.isArray(response.products)) {
        return rejectWithValue('Response data is missing products');
      }

      const products = response.products.map((product) => ({
          id: product.id,
          title: product.title,
          selectedMaterial: product.selectedMaterial,
        }));

      return {
        products: await mergeProductsWithPersistedSelections(products),
        gender,
        take,
        skip,
      };
    } catch (error) {
      console.error('Error fetching gender products:', error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Error fetching gender products'
      );
    }
  }
);

export const fetchProductById = createAsyncThunk<
  { product: IProductId; selectedMaterial: string | null; selectedSize: string | null },
  string | number,
  { rejectValue: string; state: RootState }
>(
  'Products/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response: IProductRes = await graphQLClient.request(GET_PRODUCT_BYID, { id: productId });
      const product = response.product;
      if (!product) throw new Error('Product not found');

      const { selectedMaterial: smFromProduct = null, ...productData } = product as IProductId & { selectedMaterial?: string };
      const ssFromProduct = (product as any).selectedSize;
      const productIdValue = String(productId);
      const persistedSelection = await fetchPersistedProductSelection(productIdValue);
      const materialNames = product.materialPrices?.map(({ materialName }) => materialName) || [];
      const persistedMaterial = persistedSelection?.selectedMaterial || null;
      const productMaterial = smFromProduct ?? product.materialPrices?.[0]?.materialName ?? null;
      const selectedMaterial = persistedMaterial && materialNames.includes(persistedMaterial)
        ? persistedMaterial
        : productMaterial;

      const availableSizes = getProductSizeValues(
        product.sizes,
        product.materialPrices,
        selectedMaterial
      );
      const persistedSize = persistedSelection?.selectedSize || null;
      const productSize = ssFromProduct || null;
      const selectedSize = [persistedSize, productSize, availableSizes[0] || null]
        .find((size) => !size || availableSizes.includes(size)) || null;

      return {
        product: productData as IProductId,
        selectedMaterial,
        selectedSize,
      };
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error fetching product:', error.message);
        return rejectWithValue(error.message);
      } else {
        console.error('Unknown error fetching product');
        return rejectWithValue('Unknown error');
      }
    }
  }
);

type ProductSelectionResponse = {
  getProductSelectionState: {
    id: string;
    productId?: string | null;
    selectedMaterial?: string | null;
    selectedSize?: string | null;
  } | null;
};

type ProductSelectionsResponse = {
  getProductSelectionStates: Array<{
    id: string;
    productId?: string | null;
    selectedMaterial?: string | null;
    selectedSize?: string | null;
  }>;
};

type SaveProductSelectionResponse = {
  saveProductSelectionState: {
    id: string;
    selectedMaterial?: string | null;
    selectedSize?: string | null;
  };
};

type PersistProductSelectionArgs = {
  productId: string;
  userId?: string;
  selectedMaterial: string;
  selectedSize?: string | null;
};

const fetchPersistedProductSelection = async (
  productId: string
): Promise<StoredProductSelection | null> => {
  try {
    const clientId = getProductSelectionClientId();
    const response = await graphQLClient.request<ProductSelectionResponse>(GET_PRODUCT_SELECTION, {
      productId,
      clientId,
    });

    return response.getProductSelectionState || null;
  } catch (error) {
    console.warn('Unable to load product selection:', error);
    return readStoredProductSelection(productId);
  }
};

const fetchPersistedProductSelectionsByProductIds = async (
  productIds: string[]
) => {
  const uniqueProductIds = Array.from(new Set(productIds.filter(Boolean)));
  if (!uniqueProductIds.length) return new Map<string, StoredProductSelection>();

  try {
    const response = await graphQLClient.request<ProductSelectionsResponse>(GET_PRODUCT_SELECTIONS, {
      productIds: uniqueProductIds,
      clientId: getProductSelectionClientId(),
    });

    return new Map(
      response.getProductSelectionStates
        .filter((selection) => selection.productId)
        .map((selection) => [
          String(selection.productId),
          {
            selectedMaterial: selection.selectedMaterial || null,
            selectedSize: selection.selectedSize || null,
          },
        ])
    );
  } catch (error) {
    console.warn('Unable to load product selections:', error);
    return new Map<string, StoredProductSelection>();
  }
};

const mergeProductsWithPersistedSelections = async <T extends IProducts>(
  products: T[]
) => {
  const persistedSelections = await fetchPersistedProductSelectionsByProductIds(
    products.map((product) => product.id)
  );

  if (!persistedSelections.size) return products;

  return products.map((product) => {
    const persistedSelection = persistedSelections.get(product.id);
    if (!persistedSelection?.selectedMaterial) return product;

    return {
      ...product,
      selectedMaterial: persistedSelection.selectedMaterial,
    };
  });
};

export const persistProductSelection = createAsyncThunk<
  StoredProductSelection & { productId: string },
  PersistProductSelectionArgs,
  { rejectValue: string }
>(
  'curProduct/persistProductSelection',
  async ({ productId, selectedMaterial, selectedSize }, { rejectWithValue }) => {
    const selection = { selectedMaterial, selectedSize: selectedSize || '' };

    try {
      writeStoredProductSelection(productId, selection);
      await graphQLClient.request<SaveProductSelectionResponse>(SAVE_PRODUCT_SELECTION, {
        productId,
        clientId: getProductSelectionClientId(),
        selectedMaterial,
        selectedSize: selectedSize || '',
      });

      return { ...selection, productId };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save product selection.');
    }
  }
);

export const recordProductView = createAsyncThunk<void, string, { rejectValue: string }>(
  'products/recordProductView',
  async (productId, { rejectWithValue }) => {
    try {
      await graphQLClient.request(RECORD_PRODUCT_VIEW, { productId });
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Unable to record product view.'));
    }
  }
);

export const fetchRegisterUser = createAsyncThunk<User, User, { rejectValue: string }>(
  'user/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<RegRes>(REGISTER, { data: userData });
      if (!response.createUser) {
        throw new Error('Failed to create user');
      }
      return response.createUser;
    } catch (error) {
      return rejectWithValue(mapRegisterError(error));
    }
  }
);

export const fetchLoginUser = createAsyncThunk(
  'user/login',
  async ({ email, password }: LoginReq, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<LoginRes>(LOGIN, { email, password });
      if (response.authenticateUserWithPassword?.item) {
        const sessionResponse = await graphQLClient.request<{ authenticatedItem: User }>(CURRENT);

        if (!sessionResponse.authenticatedItem) {
          return rejectWithValue(
            'Login succeeded, but the browser could not establish your account session. Please try again.'
          );
        }

        return sessionResponse.authenticatedItem;
      } else {
        return rejectWithValue('Invalid email or password');
      }
    } catch (error) {
      console.error('Error during login:', error);
      return rejectWithValue('Network error or server is down');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      await graphQLClient.request(LOGOUT);
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      return rejectWithValue('Network error or server is down');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk<CurUserPayload, void, { rejectValue: FetchUserError }>(
  'user/current',
  async (_, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{ authenticatedItem: User }>(CURRENT);
      if (!response || !response.authenticatedItem) {
        return rejectWithValue({ message: 'No user found' });
      }
      return { user: response.authenticatedItem };
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error fetching user', error);
        return rejectWithValue({ message: error.message || 'Error fetching user' });
      } else {
        console.error('Unexpected error', error);
        return rejectWithValue({ message: 'Unexpected error occurred' });
      }
    }
  }
);

export const fetchUserShip = createAsyncThunk<
  IshipAddres | null,
  { currentPage: string; selectedAddress?: number },
  { rejectValue: string }
>(
  'user/fetchUserShip',
  async ({ currentPage, selectedAddress = 0 }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        authenticatedItem: {
          shippingAddresses: IshipAddres[];
        };
      }>(GET_USER_SHIPPING);

      if (!response.authenticatedItem || !response.authenticatedItem.shippingAddresses) {
        return rejectWithValue('No shipping addresses found');
      }

      const shippingAddresses = response.authenticatedItem.shippingAddresses;

      if (currentPage === '/cart/checkout' && selectedAddress === 0) {
        const defaultAddress =
          shippingAddresses.find(address => address.useAddressForBilling) || 
          shippingAddresses[0] || 
          null;

        return defaultAddress;
      }
      if (selectedAddress < 0 || selectedAddress >= shippingAddresses.length) {
        return rejectWithValue('Invalid selected address index');
      }

      return shippingAddresses[selectedAddress];
    } catch (error) {
      return rejectWithValue('Failed to load shipping details');
    }
  }
);

export const createUserShip = createAsyncThunk<
  ShipReq, 
  { where: { id: string }, data: ShipAddressCreateInput }, 
  { rejectValue: string }
>(
  'user/createUserShip',
  async ({ where, data }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        createShippingAddress: ShipReq;
      }>(CREATE_SHIPPING_ADDRESS, { 
        userId: where.id,
        ...data
      });

      return response.createShippingAddress;
    } catch (error) {
      return rejectWithValue('Failed to update shipping information');
    }
  }
);

export const likedProductById = createAsyncThunk(
  'product/likedById',
  async (
    { productId, userId }: { productId: string; userId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      await graphQLClient.request(LIKED_PRODUCT, { productId, userId });
      const { likedProductCountAndIds } = (getState() as RootState).likedProduct;
      const currentIds = likedProductCountAndIds.ids;
      return [...currentIds, productId];
    } catch (error: any) {
      return rejectWithValue(error.response ? error.response.data : error.toString());
    }
  }
);

export const removeLikeFromProduct = createAsyncThunk(
  'product/removeLike',
  async (
    { productId, userId }: { productId: string; userId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      await graphQLClient.request(REMOVE_LIKE_FROM_PRODUCT, { productId, userId });
      const { likedProductCountAndIds } = (getState() as RootState).likedProduct;
      const currentIds = likedProductCountAndIds.ids;
      return currentIds.filter((id) => id !== productId);
    } catch (error) {
      const err = error as Error;
      return rejectWithValue(err.message || 'Something went wrong');
    }
  }
);

export const fetchLikedProduct = createAsyncThunk<IProducts[], void, { state: RootState }>(
  'product/fetchLikedProduct',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.user.user?.id;
    try {
      const response = await graphQLClient.request<{ user: { products: IProducts[] } }>(
        GET_LIKED_PRODUCT_BYID,
        { userId: userId }
      );
      const products = response.user.products.map((product) => {
        return {
          ...product,
        };
      });

      return mergeProductsWithPersistedSelections(products);
    } catch (error) {
      const err = error as Error;
      return rejectWithValue(err.message || 'Error fetching liked products');
    }
  }
);

export const fetchGuestLikedProduct = createAsyncThunk<
  IProducts[],
  void,
  { state: RootState; rejectValue: string }
>(
  'product/fetchGuestLikedProduct',
  async (_, { getState, rejectWithValue }) => {
    const ids = getState().likedProduct.likedProductCountAndIds.ids;

    if (!ids.length) return [];

    try {
      const response = await graphQLClient.request<{ products: IProducts[] }>(GET_PRODUCTS, {
        skip: 0,
        take: ids.length,
        where: { id: { in: ids } },
      });

      const productsById = new Map(response.products.map((product) => [product.id, product]));
      const products = ids
        .map((id) => productsById.get(id))
        .filter((product): product is IProducts => Boolean(product));

      return mergeProductsWithPersistedSelections(products);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Error fetching guest liked products'
      );
    }
  }
);

export const createCartItem = createAsyncThunk<
  { id: string; selectedSize?: string | null; selectedMaterial: string; price: number },
  IVariables,
  { rejectValue: string }
>(
  'cartItem/create',
  async (variables, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        createCartItem: {
          id: string;
          selectedSize?: string | null;
          selectedMaterial: string;
          price: number;
        };
      }>(
        CREATE_CART_ITEM,
        variables
      );

      return response.createCartItem;
    } catch (error) {
      return rejectWithValue('Network error or invalid');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cartItem/updateCartItem',
  async ({ id, selectedSize, selectedMaterial }: 
    { 
    id: string; 
    selectedSize: 
    string; selectedMaterial: string
  }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        updateCartItem: {
          id: string;
          selectedSize: string;
          selectedMaterial: string;
        };
      }>(UPDATE_CART_ITEM, {
        id,
        selectedSize,
        selectedMaterial,
      });

      return response.updateCartItem;
    } catch (error: any) {
      return rejectWithValue('Failed to update cart item');
    }
  }
);

export const fetchCartItem = createAsyncThunk<ICartItem[], string, { rejectValue: string }>(
  'shoppingCart/fetchCartProducts',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await withRequestTimeout(
        graphQLClient.request<{
          user: { id: string; name: string; cartItems: ICartItem[] }
        }>(
          GET_CART_ITEMS_BY_USER,
          { userId }
        ),
        'Cart is taking too long to load'
      );

      return response.user.cartItems;
    } catch (error) {
      return rejectWithValue('Failed to fetch cart products: ' + (error as Error).message);
    }
  }
);

export const fetchMaterialPriceAndImagesForCart = createAsyncThunk<
  GetMaterialPriceAndImagesRes,
  GetMaterialPriceAndImagesParams, 
  { rejectValue: string }
>(
  'material/fetchPriceAndImages',
  async ({ productId, selectedMaterial } , { rejectWithValue }) => {
    try {
      const data = await graphQLClient.request<GetMaterialPriceAndImagesRes>(
        GET_MATERIAL_END_IMG_FOR_CART_ITEM,
        { productId, selectedMaterial } 
      );
      return data;
    } catch (error: any) {
      return rejectWithValue('Error loading data image.');
    }
  }
);

export const deleteCartItem = createAsyncThunk<
  DeleteCartItem,
  string,
  { rejectValue: string }
>(
  'shoppingCart/deleteCartItem',
  async (cartItemId, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<DeleteCartItemRes>(
        DELETE_CART_ITEM, 
        { where: { id: cartItemId } }
      );
      return response.deleteCartItem;
    } catch (error) {
      const err = error as Error; 
      return rejectWithValue('Failed to delete cart item: ' + err.message);
    }
  }
);


export const incrementCartItemQuantity = createAsyncThunk<
  UpdateCartItemsRes,
  { cartItemId: string; currentQuantity: number },
  ThunkApiConfig
>(
  'shoppingCart/incrementCartItemQuantity',
  async ({ cartItemId, currentQuantity }, { rejectWithValue }) => {
    try {
      const newQuantity = currentQuantity + 1;
      const variables = {
        data: [{
          where: { id: cartItemId },
          data: { quantity: newQuantity }
        }]
      };
      const response = await graphQLClient.request<UpdateCartItemsRes>(UPDATE_CART_ITEM_QUANTITY, variables);
      return response;
    } catch (error) {
      const err = error as Error; 
      return rejectWithValue({ message: 'Failed to update cart item quantity: ' + (err.message)});
    }
  }
);

export const decrementCartItemQuantity = createAsyncThunk<
  UpdateCartItemsRes,
  { cartItemId: string; currentQuantity: number },
  ThunkApiConfig
>(
  'shoppingCart/decrementCartItemQuantity',
  async ({ cartItemId, currentQuantity }, { rejectWithValue }) => {
    try {
      const newQuantity = currentQuantity > 1 ? currentQuantity - 1 : 1;
      const variables = {
        data: [{
          where: { id: cartItemId },
          data: { quantity: newQuantity }
        }]
      };
      const response = await graphQLClient.request<UpdateCartItemsRes>(UPDATE_CART_ITEM_QUANTITY, variables);
      return response;
    } catch (error) {
      const err = error as Error; 
      return rejectWithValue({ message: 'Failed to decrement cart item quantity: ' + (err.message) });
    }
  }
);

export const updateCartItemQuantity = createAsyncThunk<
  UpdateCartItemsRes,
  { cartItemId: string; quantity: number },
  ThunkApiConfig
>(
  'shoppingCart/updateCartItemQuantity',
  async ({ cartItemId, quantity }, { rejectWithValue }) => {
    try {
      const variables = {
        data: [{
          where: { id: cartItemId },
          data: { quantity: Math.max(1, quantity) }
        }]
      };
      const response = await graphQLClient.request<UpdateCartItemsRes>(UPDATE_CART_ITEM_QUANTITY, variables);
      return response;
    } catch (error) {
      const err = error as Error;
      return rejectWithValue({ message: 'Failed to update cart item quantity: ' + err.message });
    }
  }
);



export const createPaydOrder = createAsyncThunk(
  'paydOrder/createPaydOrder',
  async ({ userId, totalAmount, items }: { userId: string; totalAmount: number; items: any[] }) => {
    try {
      const response = await graphQLClient.request<{ createPaydOrder: any }>(
        CREATE_PAYD_ORDER,
        { userId, totalAmount, items }
      );
      return response.createPaydOrder;
    } catch (error) {
      throw new Error('Failed to create PaydOrder');
    }
  }
);

export const deleteAllCartItem = createAsyncThunk<DelCartItemRes, DelCartItemArgs>(
  'cartItem/deleteCartItem',
  async (args: DelCartItemArgs) => {
    const response = await graphQLClient.request<DelCartItemRes>(
      DELETE_ALL_CART_ITEM, 
      { where: args.where }
      );
    return response;
  }
);

export const estimateZipTax = createAsyncThunk<
  { amount: number; rate: number; normalizedAddress?: string | null },
  { shippingAddress: ZipTaxAddressInput; amount: number },
  { rejectValue: string }
>(
  'payment/estimateZipTax',
  async ({ shippingAddress, amount }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        estimateZipTax: { amount: number; rate: number; normalizedAddress?: string | null };
      }>(ESTIMATE_ZIP_TAX, {
        input: shippingAddress,
        subtotal: amount,
      });

      return response.estimateZipTax;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to estimate tax';
      return rejectWithValue(message);
    }
  }
);

export const createStripePaymentIntent = createAsyncThunk(
  'payment/createStripePaymentIntent',
  async (
    {
      shippingAddress,
      guestEmail,
      guestCartItems,
    }: {
      shippingAddress?: ZipTaxAddressInput;
      guestEmail?: string;
      guestCartItems?: ICartItem[];
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const input = {
        ...(shippingAddress ? { shippingAddress } : {}),
        ...(guestEmail ? { guestEmail } : {}),
        ...(guestCartItems?.length
          ? {
              guestCartItems: guestCartItems.map((item) => ({
                productId: item.product.id,
                selectedMaterial: item.selectedMaterial,
                selectedSize: item.selectedSize || undefined,
                quantity: item.quantity,
              })),
            }
          : {}),
      };
      const variables = Object.keys(input).length ? { input } : undefined;
      const response = await graphQLClient.request<{
        createPaymentIntent: {
          clientSecret: string;
          paymentIntentId: string;
        };
      }>(CREATE_PAYMENT_INTENT, variables);

      return response.createPaymentIntent;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create payment intent'));
    }
  }
);

export const finalizeStripePaymentIntentOrder = createAsyncThunk(
  'payment/finalizeStripePaymentIntentOrder',
  async (paymentIntentId: string, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        finalizePaymentIntentOrder: {
          success: boolean;
        };
      }>(FINALIZE_PAYMENT_INTENT_ORDER, { paymentIntentId });

      return response.finalizePaymentIntentOrder;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to finalize order'));
    }
  }
);

export const createPayPalOrder = createAsyncThunk(
  'payment/createPayPalOrder',
  async (
    {
      shippingAddress,
      guestEmail,
      guestCartItems,
    }: {
      shippingAddress?: ZipTaxAddressInput;
      guestEmail?: string;
      guestCartItems?: ICartItem[];
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const variables = {
        ...(shippingAddress ? { shippingAddress } : {}),
        ...(guestEmail ? { guestEmail } : {}),
        ...(guestCartItems?.length
          ? {
              guestCartItems: guestCartItems.map((item) => ({
                productId: item.product.id,
                selectedMaterial: item.selectedMaterial,
                selectedSize: item.selectedSize || undefined,
                quantity: item.quantity,
              })),
            }
          : {}),
      };
      const response = await graphQLClient.request<{
        createPayPalOrder: {
          orderId: string;
        };
      }>(CREATE_PAYPAL_ORDER, Object.keys(variables).length ? variables : undefined);

      return response.createPayPalOrder;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create PayPal order'));
    }
  }
);

export const capturePayPalOrder = createAsyncThunk(
  'payment/capturePayPalOrder',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        capturePayPalOrder: {
          success: boolean;
        };
      }>(CAPTURE_PAYPAL_ORDER, { orderId });

      return response.capturePayPalOrder;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to capture PayPal order'));
    }
  }
);

const uploadCommentImage = async (
  file: Blob,
  commentItemId: string,
  userId: string
) => {
  const formData = new FormData();
  formData.append(
    'operations',
    JSON.stringify({
      query: CREATE_COMMENT_IMAGE,
      variables: {
        data: {
          image: null,
          commentItem: { connect: { id: commentItemId } },
          author: { connect: { id: userId } },
        },
      },
    })
  );
  formData.append('map', JSON.stringify({ '0': ['variables.data.image'] }));
  formData.append('0', file, (file as File).name || 'comment-image');

  const response = await fetch(process.env.REACT_APP_API_URL!, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'apollo-require-preflight': 'true',
      'x-apollo-operation-name': 'CreateCommentImage',
    },
    body: formData,
  });
  const result = await response.json();

  if (!response.ok || result.errors?.length) {
    throw new Error(result.errors?.[0]?.message || 'Failed to upload comment image.');
  }

  return result.data.createCommentImage;
};

export const addComment = createAsyncThunk<
  CommentItem,
  { userId: string; productId: string; content: string; existingCommentId?: string; parentId?: string; images?: File[] },
  { rejectValue: string }
>(
  'comment/addComment',
  async ({ userId, productId, content, existingCommentId, parentId, images = [] }, { rejectWithValue }) => {
    try {
      let commentId: string;

      if (existingCommentId) {
        commentId = existingCommentId;
      } else {
        const checkExistingResponse = await graphQLClient.request<{ comments: { id: string }[] }>(
        CHECK_EXISTING_COMMENT, 
        { productId: { equals: productId } });

        const existingComment = checkExistingResponse.comments[0];
        if (existingComment) {
          commentId = existingComment.id;
        } else {
          const newCommentResponse = await graphQLClient.request<{ createComment: { id: string } }>(CREATE_COMMENT, {
            userId,
            productId,
          });
          commentId = newCommentResponse.createComment.id;
        }
      }

      const response = await graphQLClient.request<{ createCommentItem: CommentItem }>(CREATE_COMMENT_ITEM, {
        data: {
          content,
          author: { connect: { id: userId } },
          comment: { connect: { id: commentId } },
          ...(parentId ? { parent: { connect: { id: parentId } } } : {}),
        },
      });

      const uploadedImages = await Promise.all(
        images.map((image) => uploadCommentImage(image, response.createCommentItem.id, userId))
      );

      return {
        ...response.createCommentItem,
        images: uploadedImages.length ? uploadedImages : response.createCommentItem.images,
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add comment');
    }
  }
);


export const fetchCommentsByProduct = createAsyncThunk<
  Comment[],
  { productId: string },
  { rejectValue: string }
>(
  'comments/fetchCommentsProduct',
  async ({ productId }, { rejectWithValue }) => {
    try {
      const data = await graphQLClient.request<FetchCommentsForProdRes>(
        GET_COMMENTS_BY_PRODUCT,
        { productId }
      );
      return data.product.comments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      return rejectWithValue('Error loading comments');
    }
  }
);

export const fetchMaterialPriceAndImagesForCurProdct = createAsyncThunk<
  MaterialPriceAndImages[],
  FetchMaterialPriceArgs,
  { rejectValue: string }
>(
  'materials/fetchMaterialPriceAndImg',
  async ({ productId, selectedMaterial }, { rejectWithValue }) => {
    try {
      const variables = { productId, selectedMaterial };
      const response = await graphQLClient.request<GetMaterialImageRes>(
        GET_IMG_BY_MATERIAL_FOR_PRODUCT,
        variables
      );

      return response.product.materialPrices;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Something went wrong');
    }
  }
);

export const fetchImagesByMaterialForHomePage = createAsyncThunk<
  { productId: string; images: ImageWithPublicUrl[]; source?: string },
  FetchMaterialPriceArgs,
  { rejectValue: string }
>(
  'materials/fetchMaterialImages',
  async ({ productId, selectedMaterial, source }, { rejectWithValue }) => {
    try {
      const variables = { productId, materialName: selectedMaterial };
      const response = await graphQLClient.request<{ productImages: ImageWithPublicUrl[] }>(
        GET_IMAGES_BY_MATERIAL_FOR_HOME_PAGE,
        variables
      );

      const images = response.productImages;

      if (images && Array.isArray(images) && images.length > 0) {
        return { productId, images, source };
      } else {
        return rejectWithValue('No images found for the selected material');
      }
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch material images');
    }
  }
);


export const fetchCountries = createAsyncThunk<
  Country[],
  void,
  { rejectValue: string }
>(
  'location/fetchCountries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<CountriesRes>(GET_COUNTRIES);
      const countries = uniqueByName(
        (response.countries || []).filter(country => Boolean(country?.name?.trim()))
      ).sort((a, b) => a.name.localeCompare(b.name));
      return countries;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch countries');
    }
  }
);

export const fetchProvinces = createAsyncThunk<
  State[],
  StateArgs,
  { rejectValue: string }
>(
  'location/fetchStatesByCountryName',
  async ({ countryName }, { rejectWithValue }) => {
    try {
      return await requestStatesByCountryName(countryName);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch states');
    }
  }
);

export const getStatesByCountryName = async (countryName: string): Promise<State[]> => {
  return requestStatesByCountryName(countryName);
};

export const fetchCartItemsCount = createAsyncThunk<
  { count: number; ids: string[] },
  string,
  { rejectValue: string }
>(
  'cart/fetchCartItemsCount',
  async (userId, { rejectWithValue }) => {
    try {
      const variables = {
        where: {
          user: {
            id: {
              equals: userId,
            },
          },
        },
      };

      const data = await graphQLClient.request<{
        cartItems: { id: string }[];
      }>(
        GET_CART_ITEMS_COUNT,
        variables
      );

      return {
        count: data.cartItems.length,
        ids: data.cartItems.map((item) => item.id),
      };
    } catch (error) {
      return rejectWithValue('Failed to fetch cart items count');
    }
  }
);

export const fetchCartItemsSelectionsByIds = createAsyncThunk<
  CartItemInfo[],
  string[],
  { rejectValue: string }
>(
  'cart/fetchCartItemsSelectionsByIds',
  async (ids, { rejectWithValue }) => {
    try {
      const variables = { ids };
      const data = await graphQLClient.request<GetCartItemsByIdsRes>(
        GET_CART_ITEM_SELECTES_MAT_SIZE,
        variables as Record<string, any>
      );
      return data.cartItems;
    } catch (error) {
      return rejectWithValue('Failed to fetch cart items details');
    }
  }
);

export const fetchLikedProductCount = createAsyncThunk<
  { count: number; ids: string[] },
  string,
  { rejectValue: string }
>(
  'likedProducts/fetchLikedProductCount',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        user: { products: { id: string }[] };
      }>(GET_LIKED_PRODUCT_COUNT, { userId });

      const products = response.user?.products || [];

      return {
        count: products.length,
        ids: products.map((product) => product.id),
      };
    } catch (error) {
      return rejectWithValue('Failed to fetch liked items count');
    }
  }
);

export const fetchResetPasswordEmail = createAsyncThunk<
  void,
  { email: string },
  { rejectValue: string }
>(
  'user/fetchResetPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<ResetPasswordRes>(
        RESET_PASSWORD_EMAIL,
        { email }
      );
      if (response.requestPasswordReset) {
        message.success('Password reset email sent.');
      } else {
        return rejectWithValue('Error sending reset email.');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error during the request.');
    }
  }
);

export const fetchResetPasswordByToken = createAsyncThunk<
  void,
  { token: string; newPassword: string; email: string },
  { rejectValue: string }
>(
  'user/fetchResetPasswordByToken',
  async ({ token, newPassword, email }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<ResetPasswordRes>(
        RESET_PASSWORD_BY_TOKEN,
        { token, newPassword, email }
      );
      
      if (response.resetPassword) {
        message.success('Password successfully reset.');
      } else {
        return rejectWithValue('Error resetting password.');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error during the request.');
    }
  }
);

export const fetchProductDetails = createAsyncThunk<
  ProductDetailsRes, 
  FetchProductDetailsArgs, 
  { rejectValue: string }
>(
  'product/fetchProductDetails',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<ProductDetailsRes>(GET_PRODUCT_DETAILS, { id });

      if (response?.product) {
        return response;
      } else {
        console.error('Product not found.');
        return rejectWithValue('Product not found.');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      return rejectWithValue(error.message || 'Error during the request.');
    }
  }
);

export const updateCurProductSize = createAsyncThunk<
  UpdateProductRes,
  UpdateProductArgs, 
  { rejectValue: string }
>(
  'curProduct/updateProductSize',
  async ({ productId, newSize }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request
      <{ updateProduct: UpdateProductRes }>(
        UPDATE_PRODUCT_SIZE, {
        where: { id: productId },
        data: { selectedSize: newSize }
      });

      if (!response.updateProduct) {
        return rejectWithValue('Product not found or update failed.')
      }

      return response.updateProduct;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error during the update request.');
    }
  }
);

export const updateCurProductMaterial = createAsyncThunk<
  UpProductMaterialRes,
  UpdateProductMatArgs,
  { rejectValue: string }
>(
  'products/updateMaterial',
  async ({ id, selectedMaterial }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<UpProductMaterialRes>(
        UPDATE_PRODUCT_MATERIAL, {
          where: { id },
          data: { selectedMaterial }
        }
      );

      if (!response.updateProduct) {
        return rejectWithValue('Product not found or update failed.');
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error during the update request.');
    }
  }
);

export const updateCurCartItemSize = createAsyncThunk<
  UpdateCartItem,
  UpdateCartItemSizeParams,
  { rejectValue: string }
>(
  'cart/updateCurCartItemSize',
  async ({ cartItemId, newSize }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<UpdateCartItemSizeResponse>(
        UPDATE_CART_ITEM_SIZE,
        { cartItemId, newSize }
      );
      return response.updateCartItem;
    } catch (error) {
      return rejectWithValue('Error updating cart item size.');
    }
  }
);

export const updateCurCartItemMaterial = createAsyncThunk<
  UpdateCartItemMaterialResponse,
  UpdateCartItemMaterialParams,
  { rejectValue: string }
>(
  'cart/updateCurCartItemMaterial',
  async ({ cartItemId, selectedMaterial, selectedSize }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request
      <UpdateCartItemMaterialResponse>(
        UPDATE_CART_ITEM_MATERIAL,
        { cartItemId, selectedMaterial, selectedSize }
      );

      return response;
    } catch (error) {
      return rejectWithValue('Error updating item in cart.');
    }
  }
);

export const updateShippingAddressCheckBox = createAsyncThunk<
  ShipAddressRes,
  { where: ShipAddressWhereUniqueInput; data: { useAddressForBilling: boolean } },
  { rejectValue: string }
>(
  'shipping/updateShippingAddressCheckBox',
  async ({ where, data }, { rejectWithValue }) => {
    try {
      if (data.useAddressForBilling) {
        const shippingResponse = await graphQLClient.request<{
          authenticatedItem: {
            shippingAddresses: IshipAddres[];
          };
        }>(GET_USER_SHIPPING);

        const shippingAddresses = shippingResponse.authenticatedItem?.shippingAddresses || [];

        await Promise.all(
          shippingAddresses
            .filter((address) => address.id && address.id !== where.id && address.useAddressForBilling)
            .map((address) =>
              graphQLClient.request<ShipAddressRes>(
                UPDATE_SHIP_ADRESS_CHECK_BOX,
                {
                  where: { id: address.id },
                  data: { useAddressForBilling: false },
                }
              )
            )
        );
      }

      const response = await graphQLClient.request<ShipAddressRes>(UPDATE_SHIP_ADRESS_CHECK_BOX, { where, data });

      return response;
    } catch (error) {
      return rejectWithValue('Error updating check box.');
    }
  }
);

export const updateShippingAddress = createAsyncThunk<
    ShipAddressRes,
    { where: ShipAddressWhereUniqueInput; data: ShipAddressUpdateInput },
    { rejectValue: string }
>(
    'shipping/updateShippingAddress',
    async ({ where, data }, { rejectWithValue }) => {
        try {
            const response = await graphQLClient.request<ShipAddressRes>(
                UPDATE_SHIP_ADRESS,
                { where, data }
            );
            return response;
        } catch (error) {
            return rejectWithValue('Error updating shipping address.');
        }
    }
);

export const deleteShippingAddress = createAsyncThunk<
  { id: string },
  { where: ShipAddressWhereUniqueInput },
  { rejectValue: string }
>(
  'shipping/deleteShippingAddress',
  async ({ where }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{ deleteShippingAddress: { id: string } }>(
        DELETE_SHIPPING_ADDRESS,
        { where }
      );

      return response.deleteShippingAddress;
    } catch (error) {
      return rejectWithValue('Error deleting shipping address.');
    }
  }
);

export const updateEmailPreference = createAsyncThunk<
  { id: string; marketingEmailsOptIn: boolean },
  { userId: string; marketingEmailsOptIn: boolean },
  { rejectValue: string }
>(
  'user/updateEmailPreference',
  async ({ userId, marketingEmailsOptIn }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        updateUser: { id: string; marketingEmailsOptIn: boolean };
      }>(UPDATE_EMAIL_PREFERENCE, {
        where: { id: userId },
        data: { marketingEmailsOptIn },
      });

      return response.updateUser;
    } catch (error) {
      return rejectWithValue('Failed to update email preferences.');
    }
  }
);

export const updateUserAvatar = createAsyncThunk<
  { id: string; avatar: User['avatar'] },
  { userId: string; file: File },
  { rejectValue: string }
>(
  'user/updateAvatar',
  async ({ userId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append(
        'operations',
        JSON.stringify({
          query: UPDATE_USER_AVATAR,
          variables: {
            where: { id: userId },
            data: { avatar: null },
          },
        })
      );
      formData.append('map', JSON.stringify({ '0': ['variables.data.avatar'] }));
      formData.append('0', file, file.name || 'profile-avatar');

      const response = await fetch(process.env.REACT_APP_API_URL!, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'apollo-require-preflight': 'true',
          'x-apollo-operation-name': 'UpdateUserAvatar',
        },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || result.errors?.length) {
        throw new Error(result.errors?.[0]?.message || 'Failed to upload profile photo.');
      }

      return result.data.updateUser;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to upload profile photo.');
    }
  }
);

export const removeUserAvatar = createAsyncThunk<
  { id: string; avatar: User['avatar'] },
  { userId: string },
  { rejectValue: string }
>(
  'user/removeAvatar',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{ updateUser: { id: string; avatar: User['avatar'] } }>(
        UPDATE_USER_AVATAR,
        { where: { id: userId }, data: { avatar: null } }
      );
      return response.updateUser;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to remove profile photo.'));
    }
  }
);

export const subscribeMarketingEmail = createAsyncThunk<
  string,
  { email: string },
  { rejectValue: string }
>(
  'user/subscribeMarketingEmail',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{ subscribeMarketingEmail: string }>(
        SUBSCRIBE_MARKETING_EMAIL,
        { email }
      );

      return response.subscribeMarketingEmail;
    } catch (error) {
      return rejectWithValue('Failed to subscribe this email.');
    }
  }
);

export const fetchMarketingSubscribers = createAsyncThunk<
  Array<{ id: string; name: string; email: string; marketingEmailsOptIn: boolean }>,
  void,
  { rejectValue: string }
>(
  'user/fetchMarketingSubscribers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{
        users: Array<{ id: string; name: string; email: string; marketingEmailsOptIn: boolean }>;
      }>(GET_MARKETING_SUBSCRIBERS);

      return response.users;
    } catch (error) {
      return rejectWithValue('Failed to fetch marketing subscribers.');
    }
  }
);

export const fetchSearchProducts = createAsyncThunk(
  'products/fetchSearchProducts',
  async (search: string, thunkAPI) => {
    try {
      const data = await graphQLClient.request<{
        products: {
          id: string;
          title: string;
          gender?: string | null;
          price?: number | null;
          productType?: {
            name?: string | null;
          } | null;
          selectedMaterial: string | null;
          images?: {
            image?: {
              id?: string;
              publicUrl: string;
            } | null;
          }[];
          materialPrices: {
            materialName: string;
            price?: number | null;
            stockQuantity?: number | null;
            productImages?: {
              image?: {
                id: string;
                publicUrl: string;
              } | null;
            }[];
          }[];
        }[];
      }>(GET_SEARCHED_PRODUCT, { search });

      const materialImages: IProductsState['materialImages'] = {};
      const products = data.products.map((product) => {
        const materialPrices = product.materialPrices ?? [];
        const matchedMaterial = materialPrices.find(
          ({ materialName }) => materialName === product.selectedMaterial
        );
        const fallbackMaterial = materialPrices.find(
          ({ productImages }) => productImages?.[0]?.image
        );
        const resolvedMaterial = matchedMaterial ?? fallbackMaterial ?? materialPrices[0];
        const previewImage = resolvedMaterial?.productImages?.[0]?.image ?? product.images?.[0]?.image;
        const selectedMaterial = resolvedMaterial?.materialName ?? product.selectedMaterial ?? '';

        if (previewImage?.id && previewImage.publicUrl) {
          materialImages[product.id] = [
            {
              name: selectedMaterial || 'default',
              image: {
                id: previewImage.id,
                publicUrl: previewImage.publicUrl,
              },
            },
          ];
        }

        return {
          id: product.id,
          title: product.title,
          selectedMaterial,
          productTypeName: product.productType?.name ?? undefined,
          gender: product.gender ?? null,
          price: resolvedMaterial?.price ?? product.price ?? null,
          stockQuantity: resolvedMaterial?.stockQuantity ?? undefined,
          materialPreview: resolvedMaterial
            ? {
                materialName: selectedMaterial,
                price: resolvedMaterial.price ?? product.price ?? 0,
                stockQuantity: resolvedMaterial.stockQuantity ?? undefined,
                productImages: (resolvedMaterial.productImages ?? []).filter(
                  (productImage): productImage is { image: { id: string; publicUrl: string } } =>
                    Boolean(productImage.image)
                ),
              }
            : undefined,
        };
      });

      return { products, materialImages };
    } catch (error) {
      return thunkAPI.rejectWithValue('Error while searching for products');
    }
  }
);

export const fetchPaydOrdersByUser = createAsyncThunk<
  { orders: paydOrder[]; total: number },
  { userId: string; take: number; skip: number },
  {
    rejectValue: string;
  }
>(
  'paydOrder/fetchPaydOrdersByUser',
  async ({ userId, take, skip }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{ paydOrders: paydOrder[]; paydOrdersCount: number }>(
        GET_PAYD_ORDERS,
        { userId, take, skip }
      );
      return {
        orders: response.paydOrders,
        total: response.paydOrdersCount,
      };
    } catch (error: any) {
      console.error('Error fetching PaydOrders:', error);
      return rejectWithValue(
        error.response?.errors?.[0]?.message || 'Failed to fetch orders'
      );
    }
  }
);

const getAdminPaydOrdersWhere = (search?: string) => {
  const normalizedSearch = search?.trim();

  if (!normalizedSearch) {
    return {};
  }

  return {
    OR: [
      { id: { equals: normalizedSearch } },
      { status: { contains: normalizedSearch, mode: 'insensitive' } },
      { paymentMethod: { contains: normalizedSearch, mode: 'insensitive' } },
      { user: { name: { contains: normalizedSearch, mode: 'insensitive' } } },
      { user: { email: { contains: normalizedSearch, mode: 'insensitive' } } },
      { items: { some: { product: { title: { contains: normalizedSearch, mode: 'insensitive' } } } } },
    ],
  };
};

export const fetchAdminPaydOrders = createAsyncThunk<
  { orders: paydOrder[]; total: number },
  { take: number; skip: number; search?: string },
  {
    rejectValue: string;
  }
>(
  'adminPaydOrders/fetchAdminPaydOrders',
  async ({ take, skip, search }, { rejectWithValue }) => {
    try {
      const response = await graphQLClient.request<{ paydOrders: paydOrder[]; paydOrdersCount: number }>(
        GET_ADMIN_PAYD_ORDERS,
        {
          take,
          skip,
          where: getAdminPaydOrdersWhere(search),
        }
      );

      return {
        orders: response.paydOrders,
        total: response.paydOrdersCount,
      };
    } catch (error: any) {
      console.error('Error fetching admin PaydOrders:', error);
      return rejectWithValue(
        error.response?.errors?.[0]?.message || 'Unable to load orders'
      );
    }
  }
);

export const fetchPriceImageForPaydOrder = createAsyncThunk(
  'product/fetchMaterialPrice',
  async (
    { productId, material }: { productId: string; material: string }
  ): Promise<{ productId: string; material: string; materialPrice: MaterialPrice | null }> => {
    const response = await graphQLClient.request<{ materialPrices: MaterialPrice[] }>(
      GET_PRICE_IMAGE_FOR_PAYD_ORDER,
      { productId, material }
    );

    return {
      productId,
      material,
      materialPrice: response.materialPrices?.[0] || null,
    };
  }
);

const getSelectedSizeValues = (sizeIds: string[] | undefined, sizes: SizeOption[]) =>
  (sizeIds || [])
    .map((sizeId) => sizes.find((size) => size.id === sizeId)?.value)
    .filter((sizeValue): sizeValue is string => Boolean(sizeValue));

const getUniqueSizeIds = (values: AdminProductFormValues) => {
  const sizeIds = values.materials.flatMap((material) =>
    material.sizeIds?.length ? material.sizeIds : values.sizeIds || []
  );

  return Array.from(new Set(sizeIds));
};

const getMaterialSizeIds = (
  material: MaterialFormItem,
  defaultSizeIds: string[] = []
) => material.sizeIds?.length ? material.sizeIds : defaultSizeIds;

const getMaterialVariantStock = (material: MaterialFormItem, sizeId: string) => {
  const stock = Number(material.variantStocks?.[sizeId]);

  return Number.isFinite(stock) ? Math.max(0, stock) : 0;
};

const getMaterialStockQuantity = (
  material: MaterialFormItem,
  materialSizeIds: string[]
) => {
  if (materialSizeIds.length) {
    return materialSizeIds.reduce(
      (total, sizeId) => total + getMaterialVariantStock(material, sizeId),
      0
    );
  }

  return Math.max(0, Number(material.stockQuantity || 0));
};

const createProductPayload = (values: AdminProductFormValues, sizes: SizeOption[]) => {
  const productSizeIds = getUniqueSizeIds(values);
  const selectedSizeValues = getSelectedSizeValues(productSizeIds, sizes);
  const primaryMaterial = values.materials?.[0]?.materialName?.trim();

  return {
    primaryMaterial,
    data: {
      title: values.title.trim(),
      description: values.description?.trim(),
      price: Number(values.price),
      gender: values.gender,
      selectedMaterial: primaryMaterial,
      selectedSize: selectedSizeValues[0] || undefined,
      productType: values.productTypeId
        ? { connect: { id: values.productTypeId } }
        : undefined,
      sizes: productSizeIds.length
        ? { connect: productSizeIds.map((id) => ({ id })) }
        : undefined,
      details: {
        create: {
          style: values.detailsStyle.trim(),
          length: values.detailsLength?.trim(),
          width: values.detailsWidth?.trim(),
          metal: values.detailsMetal?.trim(),
          setting: values.detailsSetting?.trim(),
          backing: values.detailsBacking?.trim(),
        },
      },
      materialPrices: {
        create: values.materials.map((material) => {
          const materialSizeIds = getMaterialSizeIds(material, values.sizeIds || []);

          return {
            materialName: material.materialName.trim(),
            selectedMaterial: material.materialName.trim(),
            price: Number(material.price),
            stockQuantity: getMaterialStockQuantity(material, materialSizeIds),
            sizes: materialSizeIds.length
              ? { connect: materialSizeIds.map((id) => ({ id })) }
              : undefined,
            materialIcon: material.materialIconId
              ? { connect: { id: material.materialIconId } }
              : undefined,
          };
        }),
      },
    },
  };
};

const uploadAdminProductImage = async (
  file: Blob,
  productId: string,
  materialPriceId: string
) => {
  const formData = new FormData();
  formData.append(
    'operations',
    JSON.stringify({
      query: CREATE_PRODUCT_IMAGE,
      variables: {
        data: {
          image: null,
          product: { connect: { id: productId } },
          material: { connect: { id: materialPriceId } },
        },
      },
    })
  );
  formData.append('map', JSON.stringify({ '0': ['variables.data.image'] }));
  formData.append('0', file, (file as File).name || 'upload-image');

  const response = await fetch(process.env.REACT_APP_API_URL!, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'apollo-require-preflight': 'true',
      'x-apollo-operation-name': 'CreateProduct',
    },
    body: formData,
  });
  const result = await response.json();

  if (!response.ok || result.errors?.length) {
    throw new Error(result.errors?.[0]?.message || 'Failed to upload image.');
  }
};

const uploadAdminProductAnimation = async (
  file: Blob,
  productId: string,
  materialPriceId: string
) => {
  const formData = new FormData();
  formData.append(
    'operations',
    JSON.stringify({
      query: CREATE_PRODUCT_ANIMATION,
      variables: {
        productId,
        materialPriceId,
        file: null,
      },
    })
  );
  formData.append('map', JSON.stringify({ '0': ['variables.file'] }));
  formData.append('0', file, (file as File).name || 'material-animation');

  const response = await fetch(process.env.REACT_APP_API_URL!, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'apollo-require-preflight': 'true',
      'x-apollo-operation-name': 'CreateProductAnimation',
    },
    body: formData,
  });
  const result = await response.json();

  if (!response.ok || result.errors?.length) {
    throw new Error(result.errors?.[0]?.message || 'Failed to upload animation.');
  }
};

type AdminProductVariantInventoryResponse = {
  product: CreateAdminProductResponse['createProduct'] | null;
};

const fetchCreatedProductVariantInventories = async (productId: string) => {
  const response = await graphQLClient.request<AdminProductVariantInventoryResponse>(
    GET_ADMIN_PRODUCT_VARIANT_INVENTORIES,
    { id: productId }
  );

  return response.product;
};

const createAdminProductVariantInventory = (
  productId: string,
  materialPriceId: string,
  sizeId: string,
  stockQuantity: number
) =>
  graphQLClient.request(CREATE_PRODUCT_VARIANT_INVENTORY, {
    data: {
      product: { connect: { id: productId } },
      materialPrice: { connect: { id: materialPriceId } },
      size: { connect: { id: sizeId } },
      stockQuantity,
      isAvailable: stockQuantity > 0,
    },
  });

const syncCreatedProductVariantStocks = async (
  productId: string,
  values: AdminProductFormValues,
  createdMaterials: CreateAdminProductResponse['createProduct']['materialPrices']
) => {
  const refreshedProduct = await fetchCreatedProductVariantInventories(productId);
  const materialPrices = refreshedProduct?.materialPrices?.length
    ? refreshedProduct.materialPrices
    : createdMaterials;

  for (const material of values.materials) {
    const materialSizeIds = getMaterialSizeIds(material, values.sizeIds || []);

    if (!materialSizeIds.length) {
      continue;
    }

    const materialPrice = materialPrices.find(
      (item) => item.materialName === material.materialName.trim()
    );

    if (!materialPrice) {
      continue;
    }

    for (const sizeId of materialSizeIds) {
      const stockQuantity = getMaterialVariantStock(material, sizeId);
      const existingInventory = materialPrice.variantInventories?.find(
        (inventory) => inventory.product?.id === productId && inventory.size?.id === sizeId
      );

      if (existingInventory?.id) {
        await updateAdminProductVariantStock(existingInventory.id, stockQuantity);
      } else {
        await createAdminProductVariantInventory(productId, materialPrice.id, sizeId, stockQuantity);
      }
    }
  }
};

const getMaterialFiles = (material: MaterialFormItem) =>
  (material.images || [])
    .map((fileItem) => fileItem.originFileObj)
    .filter((file): file is NonNullable<typeof file> => Boolean(file));

const getMaterialAnimationFile = (material: MaterialFormItem) =>
  (material.animation || [])
    .map((fileItem) => fileItem.originFileObj)
    .find((file): file is NonNullable<typeof file> => Boolean(file));

export const fetchAdminProductOptions = () =>
  Promise.all([
    graphQLClient.request<Pick<AdminProductOptionsResponse, 'productTypes'>>(GET_PRODUCT_TYPES),
    graphQLClient.request<Pick<AdminProductOptionsResponse, 'sizes'>>(GET_SIZES),
    graphQLClient.request<Pick<AdminProductOptionsResponse, 'materialIcons'>>(GET_MATERIAL_ICONS),
  ]).then(([typesResponse, sizesResponse, materialIconsResponse]) => ({
    productTypes: typesResponse.productTypes,
    sizes: sizesResponse.sizes,
    materialIcons: materialIconsResponse.materialIcons,
  }));

export const fetchAdminProductsPage = (variables: AdminProductsQueryVariables) =>
  graphQLClient.request<AdminProductsQueryResponse>(GET_ADMIN_PRODUCTS, {
    take: variables.take,
    skip: variables.skip,
    where: variables.search
      ? {
          OR: [
            { title: { contains: variables.search, mode: 'insensitive' } },
            { description: { contains: variables.search, mode: 'insensitive' } },
            { gender: { contains: variables.search, mode: 'insensitive' } },
            { selectedMaterial: { contains: variables.search, mode: 'insensitive' } },
            { productType: { name: { contains: variables.search, mode: 'insensitive' } } },
            { materialPrices: { some: { materialName: { contains: variables.search, mode: 'insensitive' } } } },
          ],
        }
      : {},
  });

export const fetchAdminProductMaterialDetails = (productId: string, materialPriceId: string) =>
  graphQLClient.request<{ product: Pick<AdminProduct, 'id' | 'materialPrices'> | null }>(
    GET_ADMIN_PRODUCT_MATERIAL_DETAILS,
    { productId, materialPriceId }
  );

export const searchAdminProducts = (search: string) =>
  graphQLClient.request<AdminProductsQueryResponse>(GET_SEARCHED_PRODUCT, { search });

export const createAdminProductWithImages = async (
  values: AdminProductFormValues,
  sizes: SizeOption[]
) => {
  const { primaryMaterial, data } = createProductPayload(values, sizes);

  if (!primaryMaterial) {
    throw new Error('Add at least one material.');
  }

  const createdProduct = await graphQLClient.request<CreateAdminProductResponse>(
    CREATE_PRODUCT,
    { data }
  );
  const productId = createdProduct.createProduct.id;

  await syncCreatedProductVariantStocks(
    productId,
    values,
    createdProduct.createProduct.materialPrices
  );

  for (const material of values.materials) {
    const materialName = material.materialName.trim();
    const materialPrice = createdProduct.createProduct.materialPrices.find(
      (item) => item.materialName === materialName
    );

    if (!materialPrice) {
      continue;
    }

    for (const file of getMaterialFiles(material)) {
      await uploadAdminProductImage(file, productId, materialPrice.id);
    }

    const animationFile = getMaterialAnimationFile(material);
    if (animationFile) {
      await uploadAdminProductAnimation(animationFile, productId, materialPrice.id);
    }
  }

  return createdProduct;
};

export const updateAdminProductStock = (id: string, stockQuantity: number) =>
  graphQLClient.request(UPDATE_MATERIAL_PRICE_STOCK, { id, stockQuantity });

export const updateAdminProductVariantStock = (id: string, stockQuantity: number) =>
  graphQLClient.request(UPDATE_PRODUCT_VARIANT_INVENTORY_STOCK, { id, stockQuantity });

export const updateAdminProductVariantAvailability = (id: string, isAvailable: boolean) =>
  graphQLClient.request<{ updateProductVariantInventory: { id: string; isAvailable: boolean } }>(
    UPDATE_PRODUCT_VARIANT_INVENTORY_AVAILABILITY,
    { id, isAvailable }
  );

export const updateAdminProductPrice = (id: string, price: number) =>
  graphQLClient.request(UPDATE_MATERIAL_PRICE_PRICE, { id, price: Math.round(price) });

export const updateAdminProductAvailability = (id: string, isAvailable: boolean) =>
  graphQLClient.request<{ updateMaterialPrice: { id: string; isAvailable: boolean } }>(
    UPDATE_MATERIAL_PRICE_AVAILABILITY,
    { id, isAvailable }
  );

export const updateAdminProductSizes = (id: string, sizeIds: string[]) =>
  graphQLClient.request<{ updateMaterialPrice: { id: string; sizes: SizeOption[] } }>(
    UPDATE_MATERIAL_PRICE_SIZES,
    {
      id,
      sizeIds: sizeIds.map((sizeId) => ({ id: sizeId })),
    }
  );

export const uploadAdminProductMaterialImages = async (
  productId: string,
  materialPriceId: string,
  files: Blob[]
) => {
  for (const file of files) {
    await uploadAdminProductImage(file, productId, materialPriceId);
  }
};

export const deleteAdminProductImage = (id: string) =>
  graphQLClient.request<{ deleteProductImage: { id: string } }>(DELETE_PRODUCT_IMAGE, { id });

export const deleteAdminProductCascade = (productId: string) =>
  graphQLClient.request(DELETE_PRODUCT_CASCADE, { productId });

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  createdAt?: string;
}

export const fetchAdminUsers = (variables: { take: number; skip: number; search?: string }) => {
  const normalizedSearch = variables.search?.trim();

  return graphQLClient.request<{ users: AdminUserRow[]; usersCount: number }>(GET_ADMIN_USERS, {
    take: variables.take,
    skip: variables.skip,
    where: normalizedSearch
      ? {
          OR: [
            { name: { contains: normalizedSearch, mode: 'insensitive' } },
            { email: { contains: normalizedSearch, mode: 'insensitive' } },
          ],
        }
      : {},
  });
};

export const updateUserAdminStatus = (id: string, isAdmin: boolean) =>
  graphQLClient.request<{ updateUser: AdminUserRow }>(UPDATE_USER_ADMIN_STATUS, {
    id,
    isAdmin,
  });
